import { Popover as BasePopover } from '@base-ui/react/popover';
import styled from '@emotion/styled';

import { focusRing, hoverable, pressable } from '../../interaction-states';
import { scrollbarStyles } from '../../scrollbar';
import { shadows } from '../../shadows';
import { typographyVariantStyles } from '../../typography-variants';
import { zIndex } from '../../z-index';

export const StyledCountryTrigger = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  flexShrink: 0,
  padding: 0,
  background: 'none',
  border: 'none',
  borderRadius: theme.radius[4],
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  color: theme.colors.text.primary,
  ...typographyVariantStyles.body3,
  ...hoverable(theme, 'text'),
  ...focusRing(theme, { inset: false }),

  '&:disabled': {
    cursor: 'not-allowed',
  },
}));

export const StyledFlag = styled('img')(({ theme }) => ({
  width: 16,
  height: 16,
  borderRadius: theme.radius.full,
  objectFit: 'cover',
  flexShrink: 0,
}));

export const StyledDivider = styled('span')(({ theme }) => ({
  width: 1,
  alignSelf: 'stretch',
  flexShrink: 0,
  backgroundColor: theme.colors.border.default,
}));

export const StyledChevron = styled('span', {
  shouldForwardProp: (prop) => prop !== '$open',
})<{ $open: boolean }>(({ theme, $open }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: theme.colors.icon.secondary,
  cursor: 'pointer',
  transition: 'transform 0.15s ease',
  transform: $open ? 'rotate(180deg)' : undefined,

  '& svg': {
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },
}));

export const StyledPositioner = styled(BasePopover.Positioner)({
  zIndex: zIndex.dropdown,
});

export const StyledPopup = styled(BasePopover.Popup)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  boxSizing: 'border-box',
  width: 'var(--anchor-width)',
  minWidth: 280,
  maxHeight: 320,
  padding: theme.spacing[4],
  backgroundColor: theme.colors.background.raised,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.radius[8],
  boxShadow: shadows.md,
  outline: 'none',
  transformOrigin: 'var(--transform-origin)',
  transition: 'opacity 0.15s ease, transform 0.15s ease',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'scale(0.97) translateY(-4px)',
  },
}));

export const StyledCountryList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
  ...scrollbarStyles,
});

export const StyledCountryItem = styled('button')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '20px 44px 1fr 20px',
  alignItems: 'center',
  gap: theme.spacing[8],
  padding: theme.spacing[8],
  borderRadius: theme.radius[8],
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  textAlign: 'left',
  color: theme.colors.text.primary,
  ...typographyVariantStyles.body2,
  ...hoverable(theme, 'surface'),
  ...pressable(theme, 'surfaceStrong'),
  ...focusRing(theme),

  '& > svg': {
    width: 20,
    height: 20,
    color: theme.colors.icon.brand,
  },
}));

export const StyledItemFlag = styled(StyledFlag)({
  width: 20,
  height: 20,
});

export const StyledEmpty = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: theme.spacing[12],
  textAlign: 'center',
  ...typographyVariantStyles.body3,
  color: theme.colors.text.tertiary,
}));
